import { computed, ref } from "vue"
import { defineStore } from "pinia"
import type { ActionDTO } from "@/types/gen"

export type UndoEntryType = "CREATE" | "SAVE" | "DELETE" | "COMPOUND"

export interface CreateUndoData {
    type: "CREATE"
    createdAction: ActionDTO
    parentTestCaseId: number
    insertIndex: number
    previousCurrentAction: number | null
}

export interface SaveUndoData {
    type: "SAVE"
    previousState: ActionDTO
    newState: ActionDTO
    previousCurrentAction: number | null
}

export interface DeleteUndoData {
    type: "DELETE"
    deletedAction: ActionDTO
    parentTestCaseId: number
    insertIndex: number
    previousCurrentAction: number | null
}

export interface CompoundUndoData {
    type: "COMPOUND"
    entries: UndoEntry[]
    previousCurrentAction: number | null
}

export type UndoData = CreateUndoData | SaveUndoData | DeleteUndoData | CompoundUndoData

export interface UndoEntry {
    type: UndoEntryType
    description: string
    timestamp: number
    data: UndoData
}

const MAX_STACK_SIZE = 50

export const useUndoRedoStore = defineStore("useUndoRedoStore", () => {
    const undoStack = ref<UndoEntry[]>([])
    const redoStack = ref<UndoEntry[]>([])
    const isUndoRedoing = ref(false)

    const canUndo = computed(() => undoStack.value.length > 0)
    const canRedo = computed(() => redoStack.value.length > 0)

    function pushUndo(entry: UndoEntry) {
        if (isUndoRedoing.value) return
        undoStack.value.push(entry)
        if (undoStack.value.length > MAX_STACK_SIZE) undoStack.value.shift()
        redoStack.value = []
    }

    function popUndo(): UndoEntry | undefined {
        return undoStack.value.pop()
    }

    function popRedo(): UndoEntry | undefined {
        return redoStack.value.pop()
    }

    function pushRedo(entry: UndoEntry) {
        redoStack.value.push(entry)
    }

    function clear() {
        undoStack.value = []
        redoStack.value = []
    }

    return { undoStack, redoStack, isUndoRedoing, canUndo, canRedo, pushUndo, popUndo, popRedo, pushRedo, clear }
})
